// This device's local data (03-startup-session.md): the start probe, and clearing everything.
import { readSession } from './session';

/** Opens and deletes a throw-away database; false when the browser refuses local databases. */
export function canStoreData(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const name = `desyr_probe-${Date.now()}`;
      const open = indexedDB.open(name);
      open.onsuccess = () => {
        open.result.close();
        indexedDB.deleteDatabase(name);
        resolve(true);
      };
      open.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * At most `ms` of a step that waits on the browser's databases: a deletion that a connection still
 * holds (Safari keeps one open a while after the client stops) never holds the window. The deletion
 * stays queued and finishes once the page has gone.
 */
export const atMost = <T>(work: Promise<T>, ms = 3000): Promise<T | undefined> => Promise.race([work, new Promise<undefined>((r) => setTimeout(r, ms))]);

/** Signing out's deadline from the press (03, Signing out, Deadlines, v1.58.74): steps 2 and 3 together at most 10 s. */
export const signOutBy = (now = Date.now()) => now + 10_000;
/** One request of signing out's steps 2 and 3: at most 5 s and never past `by`; a failure or no answer is ignored. */
export const capped = <T>(work: Promise<T>, by: number): Promise<T | undefined> => {
  const left = Math.min(5000, by - Date.now());
  return left > 0 ? atMost(work.catch(() => undefined), left) : (work.catch(() => {}), Promise.resolve(undefined));
};

/** desyr's own databases (all accounts', the search index, the probe). */
export const ofDesyr = (name: string) => name.startsWith('desyr') || name.startsWith('matrix-js-sdk:desyr');

/** Deletes every local database of this site (all accounts'), or those `which` picks; at most 3 s. */
export async function deleteAllDatabases(which: (name: string) => boolean = () => true): Promise<void> {
  const all = (await atMost(indexedDB.databases?.() ?? Promise.resolve([]))) ?? [];
  await atMost(
    Promise.all(
      all.map(
        (db) =>
          new Promise<void>((resolve) => {
            if (!db.name || !which(db.name)) return resolve();
            const del = indexedDB.deleteDatabase(db.name);
            del.onsuccess = del.onerror = del.onblocked = () => resolve();
          }),
      ),
    ),
  );
}

const LEFT = 'desyr_databases_left';
/**
 * A sign-out leaves this page's databases as they are and marks them (names starting with one of
 * `prefixes`): the next start in this tab deletes them, before anything opens one. Taking down the
 * chat client and its crypto store in the page that goes could hang WebKit.
 */
export function databasesLeft(prefixes: string[]): void {
  try {
    sessionStorage.setItem(LEFT, JSON.stringify([...JSON.parse(sessionStorage.getItem(LEFT) ?? '[]'), ...prefixes]));
  } catch {
    // storage refused: the in-page deletions are all there is
  }
}
/** desyr's databases altogether (the last account signed out). */
export const ALL_OF_DESYR = ['desyr', 'matrix-js-sdk:desyr'];
/** One account's: its synced data, its crypto store and its search index (33). */
export const databasesOf = (db: string, userId: string) => [`matrix-js-sdk:${db}_sync`, `${db}::`, `desyr_search_${userId}`];

/** At start: the databases a sign-out left go now. */
export function deleteDatabasesLeft(): void {
  let prefixes: string[];
  try {
    prefixes = JSON.parse(sessionStorage.getItem(LEFT) ?? '[]');
    sessionStorage.removeItem(LEFT);
  } catch {
    return;
  }
  if (prefixes.length) void deleteAllDatabases((name) => prefixes.some((p) => (p.endsWith('_sync') ? name === p : name.startsWith(p)))).catch(() => {});
}

/**
 * Clears all of this device's data and reloads (the servers are not told): "Sign out and start
 * fresh…", and the last resort of "Sign in again". Push data joins here with 28-push.md.
 */
export async function clearAllData(): Promise<never> {
  const s = readSession();
  if (s) navigator.serviceWorker?.controller?.postMessage({ type: 'desyr:session', baseUrl: s.base_url });
  await deleteAllDatabases().catch(() => {});
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // storage refused: nothing to clear
  }
  location.replace(import.meta.env.BASE_URL);
  return new Promise<never>(() => {});
}

/**
 * The window goes where signing out ends (`after`): desyr ID's end-session address, or `/login`. Without
 * its answer within 10 s the signed-out page shows anyway, unconfirmed (36, Behaviour 4.1 and 4.3),
 * whatever the request does later; once its page has loaded this one is gone, and the timer with it.
 * `assign`: this page stays in the history (Finish signing out, 4.3.2).
 */
export function leaveFor(after: string, assign = false): void {
  if (assign) location.assign(after);
  else location.replace(after);
  if (new URL(after, location.origin).origin !== location.origin) setTimeout(() => location.replace('/login'), 10_000);
}

/** What signing out keeps (03, step 5): this device's settings, the companies found, the last email,
 * and the switch-over's mark (06: later clean-ups never remove it). */
export const KEPT_ON_SIGN_OUT = ['desyr_settings', 'desyr_companies_found', 'desyr_last_email', 'desyr_switch_over'];

/** `alsoKeep`: what the accounts area needs when another account stays (33). */
export function clearStorageForSignOut(alsoKeep: string[] = []): void {
  // The sign-out's own mark (with its end-session `state`) and the sign-in generation stay (36, v1.58.63).
  const own = ['desyr_signed_out', 'desyr_auth_gen'];
  try {
    for (const key of Object.keys(localStorage)) if (!KEPT_ON_SIGN_OUT.includes(key) && !alsoKeep.includes(key) && !own.includes(key)) localStorage.removeItem(key);
    // Signed out here: the next start shows the sign-in card, never straight back in (36, v1.58.51).
    if (localStorage.getItem('desyr_signed_out') === null) localStorage.setItem('desyr_signed_out', JSON.stringify({ at: Date.now(), ended: false }));
  } catch {
    // storage refused
  }
}
